/**
 * Team Comms - Custom Hooks
 * Data fetching and state management hooks for Team Comms (Feed only)
 */
import { useState, useCallback } from 'react';
import { API_URL } from '../utils/constants';

/**
 * Hook for fetching and managing posts
 */
export function usePosts(user) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createPost = async (postData) => {
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          user_name: user.name || user.email,
          ...postData
        })
      });
      if (response.ok) {
        fetchPosts();
        return true;
      }
    } catch (err) {
      console.error('Error creating post:', err);
    }
    return false;
  };

  const deletePost = async (postId) => {
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts/${postId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setPosts(posts.filter(p => p.id !== postId));
        return true;
      }
    } catch (err) {
      console.error('Error deleting post:', err);
    }
    return false;
  };

  const updatePost = async (postId, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts/${postId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        fetchPosts();
        return true;
      }
    } catch (err) {
      console.error('Error updating post:', err);
    }
    return false;
  };

  const toggleReaction = async (postId, reactionType) => {
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts/${postId}/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          user_name: user.name || user.email,
          emoji: reactionType
        })
      });
      if (response.ok) {
        fetchPosts();
      }
    } catch (err) {
      console.error('Error toggling reaction:', err);
    }
  };

  const togglePin = async (postId, isPinned) => {
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/posts/${postId}/pin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinned: !isPinned })
      });
      if (response.ok) {
        fetchPosts();
      }
    } catch (err) {
      console.error('Error toggling pin:', err);
    }
  };

  return {
    posts,
    loading,
    fetchPosts,
    createPost,
    deletePost,
    updatePost,
    toggleReaction,
    togglePin
  };
}

/**
 * Hook for global search functionality (posts and comments only)
 */
export function useSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/comms/v3/search?q=${encodeURIComponent(query)}`);
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
        setShowSearchResults(true);
      }
    } catch (err) {
      console.error('Error searching:', err);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    showSearchResults,
    setShowSearchResults,
    searchLoading,
    performSearch,
    clearSearch
  };
}
